import React from "react";
import { useHistory } from "react-router";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Dialog } from "@syos-music/common";
import {
  TextField,
  SelectField,
  CheckboxField,
  CountryField,
} from "@syos-music/form";

import { Button, Grid, MenuItem, Tabs, Tab } from "@mui/material";
import { LoadingButton } from "@mui/lab";

const CREATE_ORDER_MUTATION = gql`
  mutation (
    $userProfile: CreateOrderAdmin_UserProfile!
    $shippingCountryId: ID!
  ) {
    createOrder: CreateOrderAdmin(
      userProfile: $userProfile
      shippingCountryId: $shippingCountryId
    ) {
      order {
        id
      }
    }
  }
`;

const REPLICATE_ORDER_MUTATION = gql`
  mutation ($orderId: ID!, $copyCart: Boolean!) {
    replicateOrder: ReplicateOrder(orderId: $orderId, copyCart: $copyCart) {
      order {
        id
      }
    }
  }
`;

export default function CreateOrderDialog({ children }) {
  const history = useHistory();

  const [activeTab, setActiveTab] = React.useState("create");

  const [isOpen, setIsOpen] = React.useState(false);
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  const [createOrder] = useMutation(CREATE_ORDER_MUTATION);
  const handleSubmitCreate = React.useCallback(
    ({ userProfile, shippingCountryId }) =>
      createOrder({
        variables: { userProfile, shippingCountryId },
      })
        .then(({ data }) => {
          close();
          history.push(`/orders/${data.createOrder.order.id}`);
        })
        .catch(() => true),
    [history, close, createOrder]
  );

  const [replicateOrder] = useMutation(REPLICATE_ORDER_MUTATION);
  const handleSubmitReplicate = React.useCallback(
    (values) =>
      replicateOrder({ variables: values })
        .then(({ data }) => {
          close();
          history.push(`/orders/${data.replicateOrder.order.id}`);
        })
        .catch(() => true),
    [history, close, replicateOrder]
  );

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog open={isOpen} onClose={close}>
        <Tabs
          value={activeTab}
          onChange={(evt, tabName) => setActiveTab(tabName)}
          variant="fullWidth"
          indicatorColor="primary"
        >
          <Tab value="create" label="Créer" />
          <Tab value="replicate" label="Répliquer" />
        </Tabs>

        {activeTab === "create" && (
          <Form onSubmit={handleSubmitCreate}>
            {({ handleSubmit, submitting, form }) => (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Dialog.Title color="primary">Créer une commande</Dialog.Title>

                <Dialog.Content>
                  <Grid container>
                    <Grid item xs={8}>
                      <TextField
                        name="userProfile.email"
                        type="email"
                        label="Email"
                        required
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <SelectField
                        name="userProfile.locale"
                        label="Langue"
                        required
                      >
                        <MenuItem value="fr">Français</MenuItem>
                        <MenuItem value="en">Anglais</MenuItem>
                      </SelectField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="userProfile.firstName"
                        label="Prénom"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="userProfile.lastName"
                        label="Nom"
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <CountryField
                        name="shippingCountryId"
                        label="Pays de livraison"
                        required
                      />

                      <OnChange name="shippingCountryId">
                        {(countryId) => {
                          form.change(
                            "userProfile.locale",
                            countryId === "FR" ? "fr" : "en"
                          );
                        }}
                      </OnChange>
                    </Grid>
                  </Grid>
                </Dialog.Content>

                <Dialog.Actions>
                  <Button variant="text" onClick={close}>
                    Annuler
                  </Button>

                  <LoadingButton type="submit" loading={submitting}>
                    Créer
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            )}
          </Form>
        )}

        {activeTab === "replicate" && (
          <Form
            onSubmit={handleSubmitReplicate}
            initialValues={{ copyCart: true }}
          >
            {({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Dialog.Title color="primary">
                  Répliquer une commande
                </Dialog.Title>

                <Dialog.Content>
                  <Grid container>
                    <Grid item xs={12} md={8}>
                      <TextField
                        name="orderId"
                        label="Identifiant de la commande"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <CheckboxField name="copyCart" label="Copier le panier" />
                    </Grid>
                  </Grid>
                </Dialog.Content>

                <Dialog.Actions>
                  <Button variant="text" onClick={close}>
                    Annuler
                  </Button>

                  <LoadingButton type="submit" loading={submitting}>
                    Répliquer
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            )}
          </Form>
        )}
      </Dialog>
    </>
  );
}
